import { alpha, createTheme, type Theme } from '@mui/material/styles';

import { brand, darkNeutrals, fontFamily, lightNeutrals, sidebar } from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    borderStrong: string;
    sidebarBackground: string;
    sidebarHover: string;
    sidebarTextDim: string;
    surfaceMuted: string;
  }
  interface PaletteOptions {
    borderStrong?: string;
    sidebarBackground?: string;
    sidebarHover?: string;
    sidebarTextDim?: string;
    surfaceMuted?: string;
  }
  // Adds `text.heading`, which extends Typography's `color` prop with a matching
  // `"textHeading"` literal (typed and autocompleted, same as the built-in "textPrimary").
  interface TypeText {
    heading: string;
  }
}

export type ThemeMode = 'dark' | 'light';

export const createAppTheme = (mode: ThemeMode): Theme => {
  const neutrals = mode === 'light' ? lightNeutrals : darkNeutrals;

  return createTheme({
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(6px)',
            backgroundColor: neutrals.background,
            borderBottom: `1px solid ${neutrals.border}`,
            boxShadow: 'none',
            color: neutrals.text,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600, textTransform: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { backgroundImage: 'none', border: `1px solid ${neutrals.border}`, boxShadow: 'none' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
        variants: [
          {
            props: { color: 'success', variant: 'filled' },
            style: {
              '& .MuiChip-icon': { color: 'inherit' },
              backgroundColor: mode === 'light' ? brand.greenBright : alpha(brand.greenBright, 0.12),
              border: `1px solid ${brand.greenBright}`,
              color: mode === 'light' ? brand.greenDeep : brand.greenBright,
            },
          },
          {
            props: { color: 'error', variant: 'filled' },
            style: {
              '& .MuiChip-icon': { color: 'inherit' },
              backgroundColor: mode === 'light' ? brand.redBright : alpha(brand.redBright, 0.12),
              border: `1px solid ${brand.redBright}`,
              color: mode === 'light' ? brand.redDeep : brand.redBright,
            },
          },
        ],
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: sidebar.background,
            borderRight: 'none',
            color: sidebar.textDim,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', border: `1px solid ${neutrals.border}` },
        },
        variants: [
          {
            props: { variant: 'elevation' },
            style: { border: 'none' },
          },
        ],
      },
    },
    palette: {
      background: { default: neutrals.background, paper: neutrals.surface },
      borderStrong: neutrals.borderStrong,
      divider: neutrals.border,
      error: { main: brand.red },
      mode,
      primary: { dark: brand.blueDark, light: brand.blueLight, main: brand.blue },
      secondary: { main: brand.blueMid },
      sidebarBackground: sidebar.background,
      sidebarHover: sidebar.hover,
      sidebarTextDim: sidebar.textDim,
      success: { main: brand.green },
      surfaceMuted: neutrals.surfaceMuted,
      text: {
        // Headings sit directly on the page background, so this needs to flip with mode —
        // the near-black navy that reads well in light mode disappears on a dark background.
        heading: mode === 'light' ? brand.blueDark : brand.blueLight,
        primary: neutrals.text,
        secondary: neutrals.textDim,
      },
      warning: { main: brand.orange },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily,
      fontSize: 14,
    },
  });
};
